import React, { useRef, useState, useEffect, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import BucketRangeButtons from './BucketRangeButtons';
import { LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
  GridComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import dayjs from 'dayjs';
import axios from 'axios';
import { FallingLines } from 'react-loader-spinner';
import { useAppContext } from '../../../providers/AppProvider';
import { isLiveTimeRange } from '../utils/chartUtils';
import { fmtValue } from '../utils/chartFormat';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
  GridComponent,
  LineChart,
  CanvasRenderer
]);

const getDefaultOptions = (
  theme,
  getThemeColor,
  firstVal,
  secondVal,
  firstKey,
  secondKey
) => {
  const values = firstVal.map(item => item[1]);
  const sValues = secondVal.map(item => item[1]);

  const dynamicMin = Math.min(...values, ...sValues).toFixed(4);
  const dynamicMax = (Math.max(...values, ...sValues) * 1.2).toFixed(2);

  return {
    color: [getThemeColor('primary'), getThemeColor('info')],
    tooltip: {
      trigger: 'axis',
      padding: 10,
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'line',
        axis: 'x'
      },
      formatter: function (params) {
        const time = dayjs(params[0].value[0]).format('HH:mm:ss');
        const rows = params
          .map(p => `${p.seriesName}: ${fmtValue(p.value[1])}`)
          .join('<br/>');
        return `Time: ${time}<br/>${rows}`;
      }
    },

    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: value => dayjs(value).format('HH:mm'),
        interval: 'auto',
        margin: 10
      },
      splitLine: {
        show: true, // Enable the vertical lines
        lineStyle: {
          color:
            theme === 'dark'
              ? getThemeColor('gray-100')
              : getThemeColor('gray-300'),
          type: 'solid' // Line style can be 'solid', 'dashed', etc.
        }
      }
    },
    yAxis: {
      position: 'right',
      axisPointer: { type: 'none' },
      axisTick: { show: false },
      splitLine: {
        show: true, // Enable the vertical lines
        lineStyle: {
          color:
            theme === 'dark'
              ? getThemeColor('gray-100')
              : getThemeColor('gray-300'),
          type: 'solid' // Line style can be 'solid', 'dashed', etc.
        }
      },
      axisLine: { show: false },
      axisLabel: {
        show: true,
        margin: 15
      },
      min: dynamicMin,
      max: dynamicMax
    },
    series: [
      {
        name: firstKey,
        type: 'line',
        data: firstVal,
        showSymbol: false,
        symbol: 'circle',
        smooth: true
      },
      {
        name: secondKey,
        type: 'line',
        data: secondVal,
        showSymbol: false,
        symbol: 'circle'
      }
    ],
    grid: {
      right: 40,
      left: 20,
      bottom: '20px',
      top: '10%',
      containLabel: true
    },
    animation: true,
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: 0,
        end: 100
      }
    ]
  };
};

const MachineDoubleLineChart = ({
  bucket,
  dynamicBucket = false,
  machineName,
  fields,
  startTime,
  endTime,
  title,
  machineStatus
}) => {
  const chartRef = useRef(null);
  const [firstVal, setFirstVal] = useState([]);
  const [secondVal, setSecondVal] = useState([]);
  const [firstKey, setFirstKey] = useState('');
  const [secondKey, setSecondKey] = useState('');
  const [loading, setLoading] = useState(true); // Track loading state
  const [currentBucket, setCurrentBucket] = useState(bucket); // Track current bucket

  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const fetchData = async () => {
    setLoading(true); // Set loading to true when fetching starts
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${currentBucket}`
      );

      const keys = Object.keys(response.data);

      let firstData = [];
      let secondData = [];

      // Iterate through keys and assign them to the correct datasets
      keys.forEach(key => {
        if (
          key === 'E75_MotorRpmValue' ||
          key === 'E45_MotorRpmValue' ||
          key === 'E75_EXTRUDER_ACTUAL_YIELD_PLC' ||
          key === 'E45_EXTRUDER_ACTUAL_YIELD_PLC' ||
          key === 'CURRENT_ACT'
        ) {
          setFirstKey(key);
          firstData = response.data[key];
        } else {
          setSecondKey(key);
          secondData = response.data[key];
        }
      });

      // Update the state
      setFirstVal(firstData);

      setSecondVal(secondData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false); // Set loading to false when fetching is done
    }
  };

  const updateBucket = bucketName => {
    setCurrentBucket(bucketName); // Update current bucket when button is clicked
  };

  const getIntervalTime = () => {
    return 60 * 1000;
  };

  useEffect(() => {
    fetchData();
    const intervalTime = getIntervalTime();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, intervalTime) : null;
    return () => clearInterval(intervalId);
  }, [currentBucket, startTime, endTime]);

  if (loading) {
    return (
      <Card className="h-0">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
          {dynamicBucket && (
            <BucketRangeButtons
              current={currentBucket}
              onChange={updateBucket}
            />
          )}
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          // `title` used to be passed here as a 5th argument to a function that
          // takes six: every name shifted one place, so the tooltip labelled the
          // first series with the card's title and dropped the second key
          // entirely. The series carry their own names now.
          option={getDefaultOptions(
            theme,
            getThemeColor,
            firstVal,
            secondVal,
            firstKey,
            secondKey
          )}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  headerContainer: {
    display: 'flex', // Flexbox for layout
    justifyContent: 'space-between', // Title on the left, buttons on the right
    alignItems: 'center',
    marginBottom: '10px' // Space between title/buttons and chart
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    // minWidth lets a long title wrap instead of shoving the resolution
    // picker out of the card: a flex item will not shrink below its
    // content width until it is told it may.
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.25,
    paddingRight: '8px'
  },
};

export default memo(MachineDoubleLineChart);
